import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc, setDoc, deleteDoc, updateDoc, serverTimestamp, writeBatch, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import PaymentMethodForm from '../../components/payments/PaymentMethodForm';
import PaymentMethodsList from '../../components/payments/PaymentMethodsList';
import paymentService from '../../services/paymentService';

import LoadingSpinner from '../../components/LoadingSpinner';

// Icons
import {
  DollarSign
} from 'lucide-react';

const Payments = () => {
  const { currentUser } = useAuth();
  const [payments, setPayments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    pendingPayments: 0,
    availableBalance: 0
  });
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);

  // Payout request state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [submittingPayout, setSubmittingPayout] = useState(false);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(true);
  const [withdrawnAmount, setWithdrawnAmount] = useState(0);
  const [payoutError, setPayoutError] = useState('');
  const [notification, setNotification] = useState(null);

  // Collection references
  const paymentMethodsRef = collection(db, 'paymentMethods');
  const payoutRequestsRef = collection(db, 'payoutRequests');

  useEffect(() => {
    const fetchPayments = async () => {
      if (!currentUser) return;

      try {
        // Fetch payments from Firestore using the payment service
        const paymentsData = await paymentService.getPaymentsByInfluencerId(currentUser.uid);
        
        // Fetch campaign details for each payment to get campaign titles
        const enhancedPayments = await Promise.all(paymentsData.map(async (payment) => {
          try {
            // Get campaign details
            const campaignRef = doc(db, 'campaigns', payment.campaignId);
            const campaignDoc = await getDoc(campaignRef);
            
            // Get brand details
            const brandRef = doc(db, 'users', payment.brandId);
            const brandDoc = await getDoc(brandRef);
            
            return {
              ...payment,
              campaignTitle: campaignDoc.exists() ? (campaignDoc.data().title || campaignDoc.data().name) : 'Unknown Campaign',
              brandName: brandDoc.exists() ? brandDoc.data().brandName : 'Unknown Brand'
            };
          } catch (error) {
            console.error(`Error fetching details for payment ${payment.id}:`, error);
            return payment;
          }
        }));
        
        setPayments(enhancedPayments);
        
        // Calculate stats
        let totalEarnings = 0;
        let pendingPayments = 0;
        
        enhancedPayments.forEach(payment => {
          if (payment.status === 'completed') {
            totalEarnings += payment.amount;
          } else {
            pendingPayments += payment.amount;
          }
        });
        
        setStats({
          totalEarnings,
          pendingPayments,
          availableBalance: totalEarnings // Will be adjusted after withdrawn amount is fetched
        });
      } catch (error) {
        console.error("Error fetching payments:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchPaymentMethods = async () => {
      if (!currentUser) return;

      try {
        // Fetch payment methods from Firestore
        const q = query(
          paymentMethodsRef,
          where('userId', '==', currentUser.uid),
          orderBy('dateAdded', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const methods = [];
        
        querySnapshot.forEach((doc) => {
          methods.push({ id: doc.id, ...doc.data() });
        });
        
        setPaymentMethods(methods);
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      } finally {
        setLoadingMethods(false);
      }
    };

    const fetchPayoutRequests = async () => {
      if (!currentUser) return;

      try {
        // Fetch all payout requests for this influencer to calculate withdrawn amount
        const q = query(
          payoutRequestsRef,
          where('influencerId', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(q);
        const requests = [];
        let totalWithdrawn = 0;
        
        querySnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          requests.push(data);
          // Count pending, completed, and approved requests as withdrawn/reserved
          if (data.status === 'pending' || data.status === 'completed' || data.status === 'approved') {
            totalWithdrawn += data.amount;
          }
        });
        
        setPayoutRequests(requests);
        setWithdrawnAmount(totalWithdrawn);
        
        // Update available balance
        setStats(prevStats => ({
          ...prevStats,
          availableBalance: prevStats.totalEarnings - totalWithdrawn
        }));
      } catch (error) {
        console.error("Error fetching payout requests:", error);
      } finally {
        setLoadingPayouts(false);
      }
    };

    fetchPayments();
    fetchPaymentMethods();
    fetchPayoutRequests();
  }, [currentUser]);
  
  // Payment method management functions
  const handleAddMethod = () => {
    setEditingMethod(null);
    setShowAddMethod(true);
  };

  const handleEditMethod = (method) => {
    setEditingMethod(method);
    setShowAddMethod(true);
  };

  const handleDeleteMethod = async (methodId) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      try {
        // Delete from Firestore
        const methodRef = doc(db, 'paymentMethods', methodId);
        await deleteDoc(methodRef);
        
        // Update state
        setPaymentMethods(prevMethods => prevMethods.filter(method => method.id !== methodId));
      } catch (error) {
        console.error("Error deleting payment method:", error);
      }
    }
  };

  const handleSetDefaultMethod = async (methodId) => {
    try {
      // Update all methods to set isDefault to false
      const batch = writeBatch(db);
      
      // Get all payment methods for this user
      const q = query(paymentMethodsRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach((doc) => {
        const methodRef = doc.ref;
        batch.update(methodRef, { isDefault: doc.id === methodId });
      });
      
      // Commit the batch
      await batch.commit();
      
      // Update state
      setPaymentMethods(prevMethods => 
        prevMethods.map(method => ({
          ...method,
          isDefault: method.id === methodId
        }))
      );
    } catch (error) {
      console.error("Error setting default payment method:", error);
    }
  };

  const handleSubmitMethod = async (methodData) => {
    try {
      if (editingMethod) {
        // Update existing method in Firestore
        const methodRef = doc(db, 'paymentMethods', editingMethod.id);
        const updatedData = {
          ...methodData,
          lastUpdated: serverTimestamp()
        };
        
        await updateDoc(methodRef, updatedData);
        
        // Update state
        const updatedMethod = {
          ...editingMethod,
          ...methodData,
          lastUpdated: new Date().toISOString()
        };
        
        setPaymentMethods(prevMethods => 
          prevMethods.map(method => 
            method.id === editingMethod.id ? updatedMethod : method
          )
        );
      } else {
        // Add new method to Firestore
        const newMethodData = {
          ...methodData,
          userId: currentUser.uid,
          dateAdded: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          // If this is the first payment method, make it default
          isDefault: paymentMethods.length === 0 ? true : methodData.isDefault || false
        };
        
        const docRef = await addDoc(paymentMethodsRef, newMethodData);
        
        // Update state
        const newMethod = {
          id: docRef.id,
          ...methodData,
          userId: currentUser.uid,
          dateAdded: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          isDefault: paymentMethods.length === 0 ? true : methodData.isDefault || false
        };
        
        setPaymentMethods(prevMethods => [...prevMethods, newMethod]);
      }
      
      // Close the form
      setShowAddMethod(false);
      setEditingMethod(null);
    } catch (error) {
      console.error("Error saving payment method:", error);
    }
  };
  
  // Alias for handleSubmitMethod to maintain compatibility with existing code
  const handleSaveMethod = handleSubmitMethod;

  // Show notification helper
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Payout request handlers
  const handleOpenPayoutModal = () => {
    setPayoutAmount('');
    setSelectedPaymentMethodId('');
    setPayoutNotes('');
    setPayoutError('');
    setShowPayoutModal(true);
  };

  const handleClosePayoutModal = () => {
    setShowPayoutModal(false);
    setPayoutAmount('');
    setSelectedPaymentMethodId('');
    setPayoutNotes('');
    setPayoutError('');
  };

  const handlePayoutAmountChange = (e) => {
    const value = e.target.value;
    // Allow only valid numeric input
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPayoutAmount(value);
      setPayoutError('');
    }
  };

  const handleSubmitPayout = async (e) => {
    e.preventDefault();
    setPayoutError('');

    // Validation
    const amount = parseFloat(payoutAmount);
    if (!payoutAmount || isNaN(amount) || amount <= 0) {
      setPayoutError('Please enter a valid amount greater than 0.');
      return;
    }

    if (amount > stats.availableBalance) {
      setPayoutError('Amount cannot exceed your available balance.');
      return;
    }

    if (!selectedPaymentMethodId) {
      setPayoutError('Please select a payment method.');
      return;
    }

    const selectedMethod = paymentMethods.find(m => m.id === selectedPaymentMethodId);
    if (!selectedMethod) {
      setPayoutError('Selected payment method is invalid.');
      return;
    }

    setSubmittingPayout(true);

    try {
      // Create payout request document in Firestore
      await addDoc(payoutRequestsRef, {
        influencerId: currentUser.uid,
        amount: amount,
        paymentMethodId: selectedPaymentMethodId,
        paymentMethodDetails: selectedMethod,
        notes: payoutNotes || '',
        status: 'pending',
        createdAt: serverTimestamp(),
        processedAt: null
      });

      // Show success notification
      showNotification('success', 'Payout request submitted successfully! Your request is now pending review.');

      // Close modal
      handleClosePayoutModal();

      // Refresh balance and payout requests
      const q = query(
        payoutRequestsRef,
        where('influencerId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);
      const requests = [];
      let totalWithdrawn = 0;
      
      querySnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        requests.push(data);
        if (data.status === 'pending' || data.status === 'completed' || data.status === 'approved') {
          totalWithdrawn += data.amount;
        }
      });
      
      setPayoutRequests(requests);
      setWithdrawnAmount(totalWithdrawn);
      setStats(prevStats => ({
        ...prevStats,
        availableBalance: prevStats.totalEarnings - totalWithdrawn
      }));
    } catch (error) {
      console.error("Error submitting payout request:", error);
      setPayoutError('Failed to submit payout request. Please try again.');
    } finally {
      setSubmittingPayout(false);
    }
  };

  const togglePaymentDetails = (paymentId) => {
    if (expandedPayment === paymentId) {
      setExpandedPayment(null);
    } else {
      setExpandedPayment(paymentId);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Format date function to handle different timestamp formats
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Pending';
    
    let date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div>
      {/* Notification */}
      {notification && (
        <div className={`mb-6 p-4 rounded-md border ${
          notification.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center">
            <span className="text-sm font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-auto text-sm font-medium hover:opacity-70"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Payments & Earnings</h2>
        <p className="text-gray-600">Track your earnings and payment history</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Total Earnings</p>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              ₱{stats.totalEarnings.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Pending Payments</p>
          <div className="mt-3 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              ₱{stats.pendingPayments.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Earnings Withdrawal / Payout Request */}
      <div className="mb-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Earnings Withdrawal</h3>
        </div>
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Available Balance</p>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-3xl font-bold text-gray-900">
                  ₱{stats.availableBalance.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Total earnings (₱{stats.totalEarnings.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                {' '}minus withdrawn/reserved (₱{withdrawnAmount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
              </p>
            </div>
            <button
              onClick={handleOpenPayoutModal}
              disabled={loading || stats.availableBalance <= 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Request Payout
            </button>
          </div>

          {/* Pending Payout Requests */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Pending Payout Requests</h4>
            {loadingPayouts ? (
              <div className="flex justify-center items-center h-24">
                <LoadingSpinner size="md" color="primary" />
              </div>
            ) : payoutRequests.filter(r => r.status === 'pending').length > 0 ? (
              <div className="space-y-3">
                {payoutRequests
                  .filter(r => r.status === 'pending')
                  .sort((a, b) => {
                    const aTime = a.createdAt?.seconds || 0;
                    const bTime = b.createdAt?.seconds || 0;
                    return bTime - aTime;
                  })
                  .map((request) => (
                    <div key={request.id} className="flex items-center justify-between border border-gray-200 rounded-md p-4">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            ₱{request.amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-gray-500">{formatDate(request.createdAt)}</p>
                        </div>
                      </div>
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Pending
                      </span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-6 border border-dashed border-gray-200 rounded-md">
                <p className="text-sm text-gray-500">No pending payout requests.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payout Request Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleClosePayoutModal}
            ></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Request Payout</h3>
                  <button
                    onClick={handleClosePayoutModal}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleSubmitPayout}>
                  {/* Available Balance Display */}
                  <div className="mb-4 bg-gray-50 border border-gray-200 rounded-md p-4">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Available Balance</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">
                      ₱{stats.availableBalance.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>

                  {/* Amount Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">₱</span>
                      </div>
                      <input
                        type="text"
                        value={payoutAmount}
                        onChange={handlePayoutAmountChange}
                        placeholder="0.00"
                        className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Payment Method Selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method <span className="text-red-500">*</span>
                    </label>
                    {loadingMethods ? (
                      <div className="flex justify-center items-center h-12">
                        <LoadingSpinner size="sm" color="primary" />
                      </div>
                    ) : paymentMethods.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        No payment methods available. Please add a payment method first.
                      </p>
                    ) : (
                      <select
                        value={selectedPaymentMethodId}
                        onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                        className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                      >
                        <option value="">Select a payment method</option>
                        {paymentMethods.map((method) => (
                          <option key={method.id} value={method.id}>
                            {method.type || method.methodType || 'Payment Method'}
                            {method.accountName ? ` - ${method.accountName}` : ''}
                            {method.isDefault ? ' (Default)' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Notes Field */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes <span className="text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      value={payoutNotes}
                      onChange={(e) => setPayoutNotes(e.target.value)}
                      rows={3}
                      placeholder="Add any notes about this payout request..."
                      className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>

                  {/* Error Display */}
                  {payoutError && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
                      <p className="text-sm text-red-700">{payoutError}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleClosePayoutModal}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingPayout || paymentMethods.length === 0}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingPayout ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Payment History</h3>
          <button className="text-sm text-primary hover:text-primary-dark flex items-center">
            Export CSV
          </button>
        </div>
        
        {!loading && payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <React.Fragment key={payment.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{payment.campaignTitle}</div>
                        <div className="text-sm text-gray-500">{payment.brandName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">₱{payment.amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(payment.status)}`}>
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.paymentDate || payment.processedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => togglePaymentDetails(payment.id)}
                          className="text-primary hover:text-primary-dark flex items-center"
                        >
                          {expandedPayment === payment.id ? (
                            <>
                              Hide
                            </>
                          ) : (
                            <>
                              View
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedPayment === payment.id && (
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Payment Details</h4>
                              <div className="space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Payment Method:</span>
                                  <span>{payment.paymentMethod || 'Not processed yet'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Reference:</span>
                                  <span>{payment.reference || 'N/A'}</span>
                                </div>
                                {payment.status === 'pending' && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Estimated Payment:</span>
                                    <span>{payment.details.estimatedPaymentDate}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Timeline</h4>
                              <div className="space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Review Submitted:</span>
                                  <span>{payment.details.reviewSubmitted}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Review Approved:</span>
                                  <span>{payment.details.reviewApproved}</span>
                                </div>
                                {payment.details.processingStarted && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Processing Started:</span>
                                    <span>{payment.details.processingStarted}</span>
                                  </div>
                                )}
                                {payment.paymentDate && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">Payment Processed:</span>
                                    <span>{payment.details.paymentProcessed}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center">
            <div className="flex justify-center mb-4">
              <DollarSign className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No payment history yet</h3>
            <p className="text-sm text-gray-500">
              Complete campaigns and submit reviews to start earning.
            </p>
          </div>
        )}
      </div>

      {/* Payment Methods Management */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Payment Methods</h3>
          <button 
            onClick={handleAddMethod}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Add Method
          </button>
        </div>
        <div className="p-6">
          {showAddMethod ? (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
              <h4 className="text-base font-medium text-gray-900 mb-4">
                {editingMethod ? 'Edit Payment Method' : 'Add Payment Method'}
              </h4>
              <PaymentMethodForm 
                onSave={handleSaveMethod} 
                onCancel={() => {
                  setShowAddMethod(false);
                  setEditingMethod(null);
                }}
                existingMethod={editingMethod}
              />
            </div>
          ) : null}
          
          {loadingMethods ? (
            <div className="flex justify-center items-center h-32">
              <LoadingSpinner size="md" color="primary" />
            </div>
          ) : (
            <PaymentMethodsList 
              paymentMethods={paymentMethods} 
              onEdit={handleEditMethod}
              onDelete={handleDeleteMethod}
              onSetDefault={handleSetDefaultMethod}
            />
          )}
          
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-yellow-800 mb-1">Important Information</h4>
            <p className="text-xs text-yellow-700">
              Your payment information is securely stored and processed. We never store complete bank account numbers or card details on our servers.
              All payment processing is handled through secure, encrypted connections.
            </p>
          </div>
        </div>
      </div>
      
      {/* Payment Information */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Payment Information</h3>
        </div>
        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-2">How Payments Work</h4>
            <p className="text-sm text-gray-600">
              Payments are processed after your review is approved by the brand. The payment timeline depends on the campaign terms, but typically follows this process:
            </p>
            <ol className="mt-2 list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>You submit your review for the campaign</li>
              <li>The brand approves your review (usually within 2-3 business days)</li>
              <li>Payment is processed according to the campaign terms (typically within 14 days of approval)</li>
              <li>You receive payment via your preferred payment method</li>
            </ol>
          </div>
          
          <div>
            <h4 className="text-base font-medium text-gray-900 mb-2">Supported Payment Methods</h4>
            <p className="text-sm text-gray-600 mb-4">
              We support the following payment methods. Add your preferred method above to receive payments.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">PP</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">PayPal</h5>
                  <p className="text-xs text-gray-500">1-2 business days</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">BT</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">Bank Transfer</h5>
                  <p className="text-xs text-gray-500">3-5 business days</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-bold">GC</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">GCash</h5>
                  <p className="text-xs text-gray-500">1-2 business days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;
